"use client"

import React, { useEffect, useMemo, useState } from "react"
import Image from "next/image"
import { INTERSHOP_APITOKEN_COOKIE_NAME } from "@/constants"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import Cookies from "js-cookie"
import { ClipboardListIcon, ShoppingCartIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import { formatPrice } from "@/lib/helper"
import { BasketData } from "@/lib/intershop/interfaces/basket/basket"
import { UserData } from "@/lib/intershop/interfaces/user/user"
import { BasketTotalData } from "@/lib/storefront/basket/basket"
import { determineBasketTotalData } from "@/lib/storefront/basket/basketHelper"
import { cn } from "@/lib/utils"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/components"
import { UbHeaderStoryblok } from "@/components/storyblok/component-types"
import { HeaderAppSettings } from "@/components/storyblok/header/header-types"

interface StoryblokShoppingBasketPopupProps {
  blok: UbHeaderStoryblok
  applicationSettings: HeaderAppSettings
  isB2B: boolean
  userData?: UserData
  isoLocale: string
  isAnonymous: boolean
  variant?: "default" | "rebrush"
  disablePopup?: boolean
}

function MiniBasketTotalData(props: {
  basketData: BasketData
  isoLocale: string
  blok: UbHeaderStoryblok
  applicationSettings: HeaderAppSettings
}) {
  const t = useTranslations("storefront")

  const itemTotal = props.basketData?.data?.totals?.grandTotal?.net?.value ?? 0
  const taxTotal = props.basketData?.data?.totals?.grandTotal?.tax?.value ?? 0
  const shippingTotal = props.basketData?.data?.totals?.shippingTotal?.net?.value ?? 0
  const ecoTaxTotal: number = props.basketData?.ecoTaxTotal ?? 0

  const totalSum = itemTotal + taxTotal + shippingTotal

  const effectiveBasketLink = useMemo(() => {
    return props.applicationSettings.basketLink && props.applicationSettings.basketLink !== ""
      ? props.applicationSettings.basketLink
      : (props.blok.basket_url?.cached_url ?? "#")
  }, [props.blok, props.applicationSettings])

  return (
    <div className="grid grid-cols-2 text-xs">
      {props.basketData.data?.totals?.grandTotal?.net && (
        <>
          <span className={"text-nowrap"}>{t("header.shopping_basket.total_positions_net")}</span>
          <span className="text-right">
            {/*// TODO: use global locale - not yet implemented*/}
            {formatPrice(
              props.isoLocale,
              props.basketData.data?.totals?.grandTotal.net.value,
              props.basketData.data?.totals?.grandTotal.net.currency
            )}
          </span>
        </>
      )}
      {props.basketData.data?.totals?.grandTotal?.tax && (
        <>
          <span className={"text-nowrap"}>{props.blok.tax_hint}</span>
          <span className="text-right">
            {/*// TODO: use global locale - not yet implemented*/}
            {formatPrice(
              props.isoLocale,
              props.basketData.data?.totals?.grandTotal.tax?.value,
              props.basketData.data?.totals?.grandTotal.tax?.currency
            )}
          </span>
        </>
      )}

      {props.basketData.data?.totals?.grandTotal?.tax && (
        <>
          <span className={"text-nowrap"}>{t("header.shopping_basket.shipping")}</span>
          <span className="text-right">
            {/*// TODO: use global locale - not yet implemented*/}

            {props.basketData.data?.totals?.shippingTotal !== undefined ? (
              <>
                {formatPrice(
                  props.isoLocale,
                  props.basketData.data?.totals?.shippingTotal?.net?.value,
                  props.basketData.data?.totals?.shippingTotal?.net?.currency
                )}
              </>
            ) : (
              <>{formatPrice(props.isoLocale, 0.0, props.basketData?.data?.totals?.grandTotal?.net?.currency)}</>
            )}
          </span>
        </>
      )}

      <span className="mt-2 font-bold text-nowrap">{t("header.shopping_basket.total_gross")}</span>
      {/*// TODO: use global locale - not yet implemented*/}
      <span className="mt-2 text-right font-bold">
        {formatPrice(props.isoLocale, totalSum, props.basketData?.data?.totals?.grandTotal?.gross?.currency)}
      </span>

      {ecoTaxTotal > 0 && (
        <>
          <span className={"text-nowrap"}>{t("header.shopping_basket.eco_tax")}</span>
          <span className="text-right">
            {
              <>
                {formatPrice(props.isoLocale, ecoTaxTotal, props.basketData?.data?.totals?.grandTotal?.net?.currency)}
              </>
            }
          </span>
        </>
      )}

      <a
        href={effectiveBasketLink}
        className="
          col-span-2 mt-7 rounded-[1.25rem] bg-primary py-2 text-center font-bold text-white uppercase
          hover:bg-primary-hover
        "
      >
        {t("header.shopping_basket.to_cart")}
      </a>
    </div>
  )
}

function MiniBasketTotalDataPatched(props: {
  isB2B: boolean
  isAnonymous: boolean
  basketData: BasketData
  isoLocale: string
  blok: UbHeaderStoryblok
  applicationSettings: HeaderAppSettings
}) {
  const isB2B = props.isB2B
  const isAnonymous = props.isAnonymous
  const isoLocale = props.isoLocale

  const { basketData, basketLineItems, basketPromotionCodes } = useUbStorefrontState()

  const basketTotalData: BasketTotalData = useMemo(() => {
    return determineBasketTotalData(isB2B, basketData, basketLineItems, basketPromotionCodes, isAnonymous, isoLocale)
  }, [basketData, basketLineItems, basketPromotionCodes, isB2B, isAnonymous, isoLocale])

  const t = useTranslations("storefront")

  const ecoTaxTotal: number = basketTotalData.ecoTaxTotal ?? 0

  const totalSum = basketTotalData.grandTotalGross

  const effectiveBasketLink = useMemo(() => {
    return props.applicationSettings.basketLink && props.applicationSettings.basketLink !== ""
      ? props.applicationSettings.basketLink
      : (props.blok.basket_url?.cached_url ?? "#")
  }, [props.blok, props.applicationSettings])

  return (
    <div className="grid grid-cols-2 text-xs">
      {props.basketData.data?.totals?.grandTotal?.net && (
        <>
          <span className={"text-nowrap"}>{t("header.shopping_basket.total_positions_net")}</span>
          <span className="text-right">
            {formatPrice(
              props.isoLocale,
              basketTotalData.discountedItemTotalNet, //PATCHED
              props.basketData.data?.totals?.grandTotal.net.currency
            )}
          </span>
        </>
      )}
      {props.basketData.data?.totals?.grandTotal?.tax && (
        <>
          <span className={"text-nowrap"}>{props.blok.tax_hint}</span>
          <span className="text-right">
            {formatPrice(
              props.isoLocale,
              basketTotalData.totalTax, //PATCHED
              props.basketData.data?.totals?.grandTotal.tax?.currency
            )}
          </span>
        </>
      )}

      {props.basketData.data?.totals?.grandTotal?.tax && (
        <>
          <span className={"text-nowrap"}>{t("header.shopping_basket.shipping")}</span>
          <span className="text-right">
            {props.basketData.data?.totals?.shippingTotal !== undefined ? (
              <>
                {formatPrice(
                  props.isoLocale,
                  props.basketData.data?.totals?.shippingTotal?.net?.value,
                  props.basketData.data?.totals?.shippingTotal?.net?.currency
                )}
              </>
            ) : (
              <>{formatPrice(props.isoLocale, 0.0, props.basketData?.data?.totals?.grandTotal?.net?.currency)}</>
            )}
          </span>
        </>
      )}

      {!isB2B && basketTotalData.paymentCostAmountGross > 0 && (
        <>
          <div className="text-nowrap">{"Abwicklungspauschale"}</div>
          <span data-testid={"paymentCostAmountGross"} className="text-right">
            {formatPrice(props.isoLocale, basketTotalData.paymentCostAmountGross, basketTotalData.currency)}
          </span>
        </>
      )}

      <span className="mt-2 font-bold text-nowrap">{t("header.shopping_basket.total_gross")}</span>
      <span className="mt-2 text-right font-bold">
        {formatPrice(props.isoLocale, totalSum, props.basketData?.data?.totals?.grandTotal?.gross?.currency)}
      </span>

      {ecoTaxTotal > 0 && (
        <>
          <span className={"text-nowrap"}>{t("header.shopping_basket.eco_tax")}</span>
          <span className="text-right">
            {
              <>
                {formatPrice(props.isoLocale, ecoTaxTotal, props.basketData?.data?.totals?.grandTotal?.net?.currency)}
              </>
            }
          </span>
        </>
      )}

      <a
        href={effectiveBasketLink}
        className="
          col-span-2 mt-7 rounded-[1.25rem] bg-primary py-2 text-center font-bold text-white uppercase
          hover:bg-primary-hover
        "
      >
        {t("header.shopping_basket.to_cart")}
      </a>
    </div>
  )
}

export default function StoryblokShoppingBasketPopup({
  blok,
  applicationSettings,
  isB2B,
  userData,
  isoLocale,
  isAnonymous,
  variant = "default",
  disablePopup = false,
}: StoryblokShoppingBasketPopupProps) {
  const t = useTranslations("storefront")
  const { basketData, setBasketData } = useUbStorefrontState()
  const [open, setOpen] = useState(false)

  const [apiTokenCookie, setApiTokenCookie] = useState(Cookies.get(INTERSHOP_APITOKEN_COOKIE_NAME))

  useEffect(() => {
    if (!apiTokenCookie || JSON.parse(apiTokenCookie).isAnonymous) {
      setBasketData(undefined)
    }
  }, [apiTokenCookie, setBasketData])

  const cookie = Cookies.get(INTERSHOP_APITOKEN_COOKIE_NAME)
  if (apiTokenCookie !== cookie) {
    setApiTokenCookie(cookie)
  }

  const totalProductQuantity: number | undefined = useMemo(() => {
    return basketData?.data?.totalProductQuantity
  }, [basketData])
  const showRebrushCount = variant === "rebrush" && !!totalProductQuantity && totalProductQuantity > 0
  const effectiveBasketLink = useMemo(() => {
    return applicationSettings.basketLink && applicationSettings.basketLink !== ""
      ? applicationSettings.basketLink
      : (blok.basket_url?.cached_url ?? "#")
  }, [blok, applicationSettings])
  const basketTotalLabel = useMemo(() => {
    const total = basketData?.data?.totals?.grandTotal?.gross ?? basketData?.data?.totals?.grandTotal?.net
    if (!userData || !total) return undefined

    return formatPrice(isoLocale, total.value, total.currency)
  }, [basketData, isoLocale, userData])

  if (disablePopup) {
    return (
      <a
        href={effectiveBasketLink}
        aria-label={t("common.shopping_basket")}
        className={cn(
          "flex h-full flex-row items-center justify-end font-semibold",
          variant === "rebrush"
            ? `
              gap-1 rounded-[4px] px-1.5 text-sm leading-4 text-[#303030]
              hover:bg-[#F8F8F8] focus-visible:ring-2 focus-visible:ring-ub-hlblue focus-visible:outline-hidden
              lg:w-auto lg:pl-1.5
            `
            : "gap-2 rounded-[1.25rem] text-gray-600 lg:w-80 lg:pl-2"
        )}
      >
        <div className="relative">
          {(variant !== "rebrush" || showRebrushCount) && (
            <span
              className={cn(
                "absolute rounded-xl px-1 text-xs font-thin text-white",
                variant === "rebrush" ? "-right-2 -top-2 bg-[#2D4B55] ring-2 ring-white" : "-mt-2 bg-primary"
              )}
            >
              {totalProductQuantity ?? ""}
            </span>
          )}
          <ShoppingCartIcon
            className={variant === "rebrush" ? "size-7 text-[#303030]" : "h-[20px] text-black"}
            strokeWidth={variant === "rebrush" ? 1.6 : 2}
          />
        </div>
        <span className="hidden whitespace-nowrap lg:block">{basketTotalLabel ?? t("common.shopping_basket")}</span>
      </a>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label={t("common.shopping_basket")}
          aria-expanded={open}
          className={cn(
            "flex h-full flex-row items-center justify-end font-semibold",
            variant === "rebrush"
              ? `
                gap-1 rounded px-1.5 text-sm text-[#303030]
                hover:bg-[#F8F8F8] focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-hidden
                lg:w-auto lg:pl-1.5
              `
              : "gap-2 rounded-[1.25rem] text-gray-600 lg:w-80 lg:pl-2"
          )}
        >
          <p className="hidden whitespace-nowrap lg:block">{t("common.shopping_basket")}</p>
          <div className="relative">
            {(variant !== "rebrush" || showRebrushCount) && (
              <span
                className={cn(
                  "absolute rounded-xl px-1 text-xs font-thin text-white",
                  variant === "rebrush" ? "-right-2 -top-2 bg-[#2D4B55] ring-2 ring-white" : "-mt-2 bg-primary"
                )}
              >
                {totalProductQuantity ?? ""}
              </span>
            )}
            <ShoppingCartIcon
              className={variant === "rebrush" ? "size-7 text-[#303030]" : "h-[20px] text-black"}
              strokeWidth={variant === "rebrush" ? 1.6 : 2}
            />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="flex w-[260px] flex-col gap-y-4 rounded-none border-black p-4">
        <div className="flex w-full items-center justify-between">
          <Image src="/img/baer_transparent.png" alt="Logo" width={40} height={40} />
          <span className="text-lg font-bold">
            {totalProductQuantity ?? ""} {t(`common.${totalProductQuantity === 1 ? "item" : "items"}`)}
          </span>
        </div>

        {basketData && totalProductQuantity && totalProductQuantity > 0 ? (
          <>
            {!isB2B && applicationSettings.usePatchedMiniCartView ? (
              <>
                {" "}
                <MiniBasketTotalDataPatched
                  isB2B={isB2B}
                  isAnonymous={isAnonymous}
                  basketData={basketData}
                  isoLocale={isoLocale}
                  blok={blok}
                  applicationSettings={applicationSettings}
                />
              </>
            ) : (
              <>
                <MiniBasketTotalData
                  basketData={basketData}
                  isoLocale={isoLocale}
                  blok={blok}
                  applicationSettings={applicationSettings}
                />
              </>
            )}
          </>
        ) : (
          <>
            <span>{t("header.empty_shopping_basket")}</span>
            <a href={blok.quick_order_url?.cached_url} className="flex items-center text-sm">
              <ClipboardListIcon className="mr-2.5 text-primary"></ClipboardListIcon>
              <span>{t("header.quick_order")}</span>
            </a>

            <p className="text-xs">
              {t(
                `header.pricing_description.${(userData && userData.customerType == "SMB") || (!userData && isB2B) ? "b2b" : "b2c"}`
              )}
            </p>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}
